import { useEffect, useRef, useState } from 'react'
import { useAudioPlayerStore } from '../store/audioPlayerStore'

declare global {
  interface Window {
    SC?: {
      Widget: {
        (iframe: HTMLIFrameElement): any
        Events: {
          READY: string
          PLAY: string
          PAUSE: string
          FINISH: string
          PLAY_PROGRESS: string
        }
      }
    }
  }
}

export default function GlobalAudioPlayer({
  bottomOffset = 16,
  forceHidden = false,
  maxWidthPx,
}: {
  bottomOffset?: number
  forceHidden?: boolean
  maxWidthPx?: number
}) {
  const StepForwardIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M5 5v14h3V5m2 0v14l11-7" />
    </svg>
  )

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const soundcloudIframeRef = useRef<HTMLIFrameElement | null>(null)
  const soundcloudWidgetRef = useRef<any>(null)
  const soundcloudPollRef = useRef<number | null>(null)
  const progressRef = useRef<HTMLDivElement | null>(null)
  const scrubbingRef = useRef(false)
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [soundcloudScriptReady, setSoundcloudScriptReady] = useState(Boolean(window.SC?.Widget))

  const {
    queue,
    activeIndex,
    wantedPlaying,
    isPlaying,
    currentTime,
    duration,
    setPlaybackState,
    setCurrentTime,
    setDuration,
    requestPause,
    playNext,
  } = useAudioPlayerStore()

  const activeTrack = activeIndex >= 0 ? queue[activeIndex] : null
  const isSoundCloudTrack = activeTrack?.provider === 'soundcloud'
  const progressPct = duration > 0 ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) : 0
  const hasMultipleTracks = queue.length > 1
  const hasTrackTitle = Boolean(activeTrack?.title?.trim())
  const showNext = hasMultipleTracks && hasTrackTitle
  const hasTrackText = Boolean(activeTrack?.title || activeTrack?.subtext)
  const showHeader = hasTrackText || showNext

  useEffect(() => {
    if (window.SC?.Widget) {
      setSoundcloudScriptReady(true)
      return
    }

    const existing = document.querySelector('script[data-soundcloud-widget="true"]') as HTMLScriptElement | null
    if (existing) {
      const onLoad = () => setSoundcloudScriptReady(true)
      existing.addEventListener('load', onLoad)
      return () => existing.removeEventListener('load', onLoad)
    }

    const script = document.createElement('script')
    script.src = 'https://w.soundcloud.com/player/api.js'
    script.async = true
    script.dataset.soundcloudWidget = 'true'
    script.onload = () => setSoundcloudScriptReady(true)
    document.body.appendChild(script)
  }, [])

  const stopSoundcloudPolling = () => {
    if (soundcloudPollRef.current != null) {
      window.clearInterval(soundcloudPollRef.current)
      soundcloudPollRef.current = null
    }
  }

  const startSoundcloudPolling = () => {
    stopSoundcloudPolling()
    soundcloudPollRef.current = window.setInterval(() => {
      const widget = soundcloudWidgetRef.current
      if (!widget) return
      widget.getPosition((ms: number) => setCurrentTime((ms || 0) / 1000))
      widget.getDuration((ms: number) => setDuration((ms || 0) / 1000))
    }, 350)
  }

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    if (!activeTrack) {
      el.pause()
      el.removeAttribute('src')
      el.load()
      return
    }
    if (activeTrack.provider === 'soundcloud') {
      el.pause()
      el.removeAttribute('src')
      el.load()
      return
    }

    const srcChanged = el.src !== activeTrack.audioUrl
    if (srcChanged) {
      // Hard-stop previous audio before switching source.
      el.pause()
      el.currentTime = 0
      el.src = activeTrack.audioUrl
      el.load()
    }
  }, [activeTrack])

  useEffect(() => {
    const el = audioRef.current
    if (!activeTrack) return

    if (activeTrack.provider === 'soundcloud') {
      const widget = soundcloudWidgetRef.current
      if (!widget) return

      if (!wantedPlaying) {
        setPlaybackState(false)
        widget.pause()
        return
      }

      document.querySelectorAll('audio').forEach((node) => {
        if (node !== el) node.pause()
      })
      widget.play()
      return
    }

    if (!el) return

    if (!wantedPlaying) {
      // Keep UI state in sync on mobile where pause event can be delayed.
      setPlaybackState(false)
      el.pause()
      return
    }

    // Ensure only one audio source plays at a time across the page.
    document.querySelectorAll('audio').forEach((node) => {
      if (node !== el) node.pause()
    })

    const playPromise = el.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        setPlaybackState(false)
        requestPause()
      })
    }
  }, [activeTrack, wantedPlaying, requestPause, setPlaybackState])

  useEffect(() => {
    if (!activeTrack || activeTrack.provider !== 'soundcloud' || !soundcloudScriptReady || !soundcloudIframeRef.current || !window.SC?.Widget) {
      soundcloudWidgetRef.current = null
      stopSoundcloudPolling()
      return
    }

    const widget = window.SC.Widget(soundcloudIframeRef.current)
    soundcloudWidgetRef.current = widget

    widget.bind(window.SC.Widget.Events.READY, () => {
      widget.getDuration((ms: number) => setDuration((ms || 0) / 1000))
      widget.getPosition((ms: number) => setCurrentTime((ms || 0) / 1000))
      if (wantedPlaying) widget.play()
    })
    widget.bind(window.SC.Widget.Events.PLAY, () => {
      setPlaybackState(true)
      startSoundcloudPolling()
    })
    widget.bind(window.SC.Widget.Events.PAUSE, () => {
      setPlaybackState(false)
      stopSoundcloudPolling()
    })
    widget.bind(window.SC.Widget.Events.FINISH, () => {
      stopSoundcloudPolling()
      playNext()
    })
    widget.bind(window.SC.Widget.Events.PLAY_PROGRESS, (event: { currentPosition?: number }) => {
      setCurrentTime(((event?.currentPosition || 0)) / 1000)
    })

    return () => {
      stopSoundcloudPolling()
      try {
        widget.pause()
      } catch { }
    }
  }, [activeTrack, playNext, setCurrentTime, setDuration, setPlaybackState, soundcloudScriptReady, wantedPlaying])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    const onLoaded = () => setDuration(el.duration || 0)
    const onTime = () => setCurrentTime(el.currentTime || 0)
    const onPlay = () => setPlaybackState(true)
    const onPause = () => setPlaybackState(false)
    const onEnded = () => playNext()

    el.addEventListener('loadedmetadata', onLoaded)
    el.addEventListener('durationchange', onLoaded)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('ended', onEnded)

    return () => {
      el.removeEventListener('loadedmetadata', onLoaded)
      el.removeEventListener('durationchange', onLoaded)
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('ended', onEnded)
    }
  }, [playNext, setCurrentTime, setDuration, setPlaybackState])

  const seek = (nextValue: number) => {
    if (!duration) return
    if (activeTrack?.provider === 'soundcloud') {
      const widget = soundcloudWidgetRef.current
      if (!widget) return
      const nextTime = Math.max(0, Math.min(duration, nextValue))
      widget.seekTo(nextTime * 1000)
      setCurrentTime(nextTime)
      return
    }

    const el = audioRef.current
    if (!el) return
    const nextTime = Math.max(0, Math.min(duration, nextValue))
    el.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const seekFromClientX = (clientX: number) => {
    const track = progressRef.current
    if (!track || !duration) return
    const rect = track.getBoundingClientRect()
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0
    seek(ratio * duration)
  }

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!scrubbingRef.current) return
      seekFromClientX(e.clientX)
    }
    const onUp = () => {
      scrubbingRef.current = false
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [duration])

  const shouldShow = Boolean(activeTrack) && !forceHidden && (isPlaying || wantedPlaying)

  useEffect(() => {
    if (shouldShow) {
      setIsMounted(true)
      const t = window.setTimeout(() => setIsVisible(true), 24)
      return () => window.clearTimeout(t)
    }

    setIsVisible(false)
    if (!isMounted) return

    const t = window.setTimeout(() => {
      setIsMounted(false)
    }, 320)
    return () => window.clearTimeout(t)
  }, [isMounted, shouldShow])

  if (!activeTrack) return <audio ref={audioRef} preload="metadata" />

  const widthStyle = maxWidthPx
    ? `min(calc(100vw - 16px), ${Math.max(180, Math.round(maxWidthPx))}px)`
    : 'calc(100vw - 16px)'

  return (
    <>
      <audio ref={audioRef} preload="metadata" />
      {isSoundCloudTrack ? (
        <iframe
          key={activeTrack.audioUrl}
          ref={soundcloudIframeRef}
          title="Hidden SoundCloud playback"
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(activeTrack.audioUrl)}&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false&buying=false&sharing=false&download=false`}
          className="pointer-events-none fixed left-[-9999px] top-[-9999px] h-[1px] w-[1px] opacity-0"
          allow="autoplay; encrypted-media"
        />
      ) : null}
      {isMounted ? (
        <div
          className={`global-audio-player fixed left-1/2 z-[40] bg-[color-mix(in_srgb,var(--surface-1)_54%,transparent)] backdrop-blur-sm ${isVisible ? 'is-visible' : ''
            }`}
          style={{
            bottom: `${bottomOffset}px`,
            width: widthStyle,
            borderRadius: '14px',
          }}
        >
          {showHeader ? (
            <div className="flex items-center gap-2 px-3 pt-2 pb-1.5 text-white [mix-blend-mode:difference]">
              {hasTrackText ? (
                <div className="min-w-0 flex-1">
                  {activeTrack.title ? <p className="truncate font-serif text-[15px] font-medium opacity-90">{activeTrack.title}</p> : null}
                  {activeTrack.subtext ? (
                    <p className="truncate font-serif text-[14px] opacity-62">{activeTrack.subtext}</p>
                  ) : null}
                </div>
              ) : null}

              <div className="ml-auto flex items-center gap-1">
                {showNext ? (
                  <button
                    onClick={playNext}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-black opacity-88 transition hover:opacity-100"
                    style={{ mixBlendMode: 'normal' }}
                    aria-label="Next track"
                  >
                    <StepForwardIcon size={13} />
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className={`${showHeader ? 'px-3 pb-2' : 'px-3 py-2'} text-white [mix-blend-mode:difference]`}>
            <div
              ref={progressRef}
              className="relative h-[4px] w-full cursor-pointer overflow-hidden rounded-full"
              style={{ backgroundColor: 'color-mix(in srgb, currentColor 18%, transparent)' }}
              onClick={(e) => seekFromClientX(e.clientX)}
              onPointerDown={(e) => {
                scrubbingRef.current = true
                seekFromClientX(e.clientX)
              }}
              role="slider"
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={Math.floor(duration || 0)}
              aria-valuenow={Math.floor(currentTime || 0)}
            >
              <div className="absolute left-0 top-0 h-full bg-current" style={{ width: `${progressPct}%` }} />
              <div
                className="absolute top-1/2 h-[8px] w-[8px] -translate-y-1/2 rounded-full bg-current"
                style={{ left: `calc(${progressPct}% - 4px)` }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
